import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { SharedLayout } from '../components/layout/SharedLayout';
import { theme } from '../styles/theme';

const HeroSection = styled.section`
  background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%);
  padding: 100px 20px 80px;
  margin-top: -80px;
  padding-top: 140px;
  color: white;
  text-align: center;
`;

const Container = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: clamp(36px, 5vw, 56px);
  font-weight: 800;
  margin-bottom: 20px;
`;

const PageSubtitle = styled.p`
  font-size: clamp(18px, 2.5vw, 24px);
  opacity: 0.9;
  line-height: 1.5;
`;

const ContentSection = styled.section`
  padding: 80px 20px;
  background: white;
`;

const TransparencyCard = styled.div`
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  padding: 40px;
  border-radius: 20px;
  margin-bottom: 60px;
  text-align: center;
`;

const CardTitle = styled.h2`
  font-size: 32px;
  font-weight: 800;
  color: #333;
  margin-bottom: 20px;
`;

const CardText = styled.p`
  font-size: 20px;
  color: #333;
  font-weight: 600;
  line-height: 1.6;
`;

const PortfolioSection = styled.div`
  margin-bottom: 60px;
`;

const SectionTitle = styled.h3`
  font-size: 28px;
  font-weight: 800;
  color: #333;
  margin-bottom: 30px;
`;

const PortfolioGrid = styled.div`
  display: grid;
  gap: 30px;
`;

const PortfolioItem = styled.div`
  background: #f8f9fa;
  padding: 30px;
  border-radius: 15px;
  border-left: 4px solid #ffd700;
`;

const ProductName = styled.h4`
  font-size: 20px;
  font-weight: 700;
  color: #333;
  margin-bottom: 15px;
`;

const ProductDetails = styled.p`
  font-size: 16px;
  color: #666;
  line-height: 1.6;
`;

const WhySection = styled.div`
  background: white;
  padding: 40px;
  border-radius: 20px;
  box-shadow: 0 5px 30px rgba(0, 0, 0, 0.08);
`;

const ReasonList = styled.ul`
  list-style: none;
  padding: 0;
`;

const ReasonItem = styled.li`
  font-size: 18px;
  color: #666;
  line-height: 1.8;
  margin-bottom: 20px;
  padding-left: 30px;
  position: relative;
  
  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #ffd700;
    font-weight: bold;
    font-size: 20px;
  }
`;

const CTASection = styled.section`
  background: #f8f9fa;
  padding: 80px 20px;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: 36px;
  font-weight: 800;
  color: #333;
  margin-bottom: 20px;
`;

const CTAText = styled.p`
  font-size: 20px;
  color: #666;
  margin-bottom: 40px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButton = styled(Link)`
  background: #232B3B;
  color: white !important;
  padding: 18px 40px;
  border-radius: 8px;
  text-decoration: none;
  font-size: 18px;
  font-weight: 600;
  display: inline-block;
  transition: all 0.3s;
  margin: 0 10px;
  
  &:hover {
    background: #1a1f2e;
    transform: translateY(-2px);
    color: white !important;
  }
`;

const PhoneButton = styled.a`
  background: transparent;
  color: #232B3B;
  padding: 18px 40px;
  border: 2px solid #232B3B;
  border-radius: 8px;
  text-decoration: none;
  font-size: 18px;
  font-weight: 600;
  display: inline-block;
  transition: all 0.3s;
  margin: 0 10px;
  
  &:hover {
    background: #232B3B;
    color: white !important;
    transform: translateY(-2px);
  }
`;

export const WhatWeOwn: React.FC = () => {
  return (
    <SharedLayout>
      <HeroSection>
        <Container>
          <PageTitle>What We Own</PageTitle>
          <PageSubtitle>
            We only recommend products we own ourselves. Full transparency, no exceptions.
          </PageSubtitle>
        </Container>
      </HeroSection>

      <ContentSection>
        <Container>
          <TransparencyCard>
            <CardTitle>Our Promise</CardTitle>
            <CardText>
              We show you exactly what we own - not just what we sell. 
              Ask any other advisor to do the same.
            </CardText>
          </TransparencyCard>

          <PortfolioSection>
            <SectionTitle>Our Personal Portfolios</SectionTitle>
            <PortfolioGrid>
              <PortfolioItem>
                <ProductName>Fixed Index Annuities with S&P 500</ProductName>
                <ProductDetails>
                  Uncapped participation rates with Standard & Poor's 500 indexing. 
                  No losses during down years, full participation in market volatility during up years.
                </ProductDetails>
              </PortfolioItem>
              
              <PortfolioItem>
                <ProductName>Date Lottery Elimination Strategy</ProductName>
                <ProductDetails>
                  Multiple annuities with staggered anniversary dates to eliminate timing risk. 
                  Systematic allocation throughout the year for consistent returns.
                </ProductDetails>
              </PortfolioItem>
              
              <PortfolioItem>
                <ProductName>Long-Term Growth Focus</ProductName>
                <ProductDetails>
                  10+ year surrender periods for maximum growth potential. 
                  Designed for serious retirement planning, not short-term gains.
                </ProductDetails>
              </PortfolioItem>
            </PortfolioGrid>
          </PortfolioSection>

          <WhySection>
            <SectionTitle>Why These Products?</SectionTitle>
            <ReasonList>
              <ReasonItem>
                40+ years of proven performance through multiple market cycles
              </ReasonItem>
              <ReasonItem>
                Zero losses during 2008 financial crisis and 2020 pandemic
              </ReasonItem>
              <ReasonItem>
                Backtested using our proprietary AI-powered analysis tools
              </ReasonItem>
              <ReasonItem>
                Millions of our own dollars invested using the exact same strategy
              </ReasonItem>
              <ReasonItem>
                Products selected through the Averill Method, not commissions
              </ReasonItem>
            </ReasonList>
          </WhySection>
        </Container>
      </ContentSection>

      <CTASection>
        <Container>
          <CTATitle>See Our Analysis</CTATitle>
          <CTAText>
            Want to see the actual numbers behind our portfolio choices? 
            Schedule a consultation to review our backtesting results.
          </CTAText>
          <div>
            <CTAButton to="/retirevana">
              Schedule Consultation
            </CTAButton>
            <PhoneButton href="tel:800-922-1894">
              Call 800-922-1894
            </PhoneButton>
          </div>
        </Container>
      </CTASection>
    </SharedLayout>
  );
};